import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  PLATFORM_ID,
  booleanAttribute,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SongsService } from '../../services/songs.service';
import { ButtonComponent } from '../button/button.component';
import { formatNumber } from '../../utils/formatters';

@Component({
  selector: 'app-album-profile-preview-drawer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ButtonComponent],
  templateUrl: './album-profile-preview-drawer.component.html',
  host: {
    class: 'contents',
  },
})
export class AlbumProfilePreviewDrawerComponent {
  private songsService = inject(SongsService);
  private platformId = inject(PLATFORM_ID);

  readonly songChartsFixedPush = input(false, { transform: booleanAttribute });
  readonly mode = input<'inline' | 'fixed' | 'auto'>('auto');
  readonly sidebarOpenChange = output<boolean>();
  readonly songClick = output<string>();

  selectedAlbumId = signal<string | null>(null);
  albumDetails = signal<any | null>(null);
  loading = signal<boolean>(false);
  hasOpened = signal<boolean>(false);

  readonly isOpen = computed(() => this.selectedAlbumId() !== null);

  readonly albumDiscs = computed<{ discNumber: number; tracks: any[] }[]>(() => {
    const album = this.albumDetails();
    if (!album || !album.songs || album.songs.length === 0) return [];

    const discMap = new Map<number, any[]>();
    for (const song of album.songs) {
      const discNum = song.discNumber || 1;
      if (!discMap.has(discNum)) {
        discMap.set(discNum, []);
      }
      discMap.get(discNum)!.push(song);
    }

    const sortedDiscNumbers = Array.from(discMap.keys()).sort((a, b) => a - b);
    return sortedDiscNumbers.map((discNum) => ({
      discNumber: discNum,
      tracks: discMap.get(discNum)!.sort((a, b) => (a.trackNumber || 0) - (b.trackNumber || 0)),
    }));
  });

  readonly hasMultipleDiscs = computed(() => this.albumDiscs().length > 1);

  readonly isFixed = computed(() => {
    const m = this.mode();
    if (m === 'fixed') return true;
    if (m === 'inline') return false;
    return this.songChartsFixedPush();
  });

  readonly asideNgClass = computed(() => {
    const open = this.isOpen();
    const hasOpened = this.hasOpened();
    if (this.isFixed()) {
      return {
        'fixed top-[88px] right-4 z-[100] flex w-full md:w-[380px] md:h-[calc(100vh-104px)] md:rounded-2xl md:border flex-col overflow-hidden bg-sidebar-panel-bg max-md:right-0 max-md:top-[72px] max-md:h-[calc(100vh-72px)] max-md:border-l max-md:border-[#27272A] max-md:shadow-2xl':
          true,
        'transition-transform duration-300 ease-in-out': hasOpened,
        'translate-x-0 border-sidebar-panel-border album-drawer-fixed-shadow md:border-sidebar-panel-border': open,
        'translate-x-full md:translate-x-[calc(100%+1rem)] pointer-events-none border-transparent shadow-none max-md:border-transparent md:border-transparent md:shadow-none':
          !open,
      };
    }
    return {
      'max-md:fixed max-md:inset-y-0 max-md:right-0 max-md:z-[250] max-md:w-full max-md:border-sidebar-panel-border max-md:border-l bg-sidebar-panel-bg': true,
      'transition-all duration-300 ease-in-out will-change-[transform,opacity] transform-gpu': hasOpened,
      'max-md:translate-x-0': open,
      'max-md:translate-x-full max-md:pointer-events-none': !open,
      'md:absolute md:inset-0 md:w-full md:h-full md:min-h-0 md:overflow-hidden md:border md:border-sidebar-panel-border md:shadow-xl md:rounded-xl': true,
      'md:opacity-100 md:z-30 opacity-100': open,
      'md:opacity-0 md:z-10 md:pointer-events-none opacity-0 pointer-events-none': !open,
    };
  });

  async open(id: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    this.hasOpened.set(true);
    const resolvedId = String(id).trim();
    if (!resolvedId) return;

    this.selectedAlbumId.set(resolvedId);
    this.sidebarOpenChange.emit(true);
    requestAnimationFrame(() => {
      document.getElementById('album-profile-preview-drawer-scroll')?.scrollTo({ top: 0 });
    });

    this.loading.set(true);
    this.albumDetails.set(null);

    try {
      const response = await firstValueFrom(this.songsService.getAlbumDetailed(resolvedId));
      if (this.selectedAlbumId() !== resolvedId) return;
      this.albumDetails.set(response.data?.album ?? null);
    } catch (error) {
      console.error('Failed to load album details:', error);
    } finally {
      if (this.selectedAlbumId() === resolvedId) {
        this.loading.set(false);
      }
    }
  }

  closeSidebar(): void {
    this.selectedAlbumId.set(null);
    this.sidebarOpenChange.emit(false);
    setTimeout(() => {
      this.albumDetails.set(null);
      this.loading.set(false);
    }, 300);
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    if (this.isOpen() && this.isFixed()) {
      this.closeSidebar();
    }
  }

  playOnSpotify(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const spotifyId = this.albumDetails()?.spotifyId;
    if (spotifyId) {
      window.open(`https://open.spotify.com/album/${spotifyId}`, '_blank', 'noopener,noreferrer');
    }
  }

  formatDuration(durationMs: number | null): string {
    if (!durationMs) return '--:--';
    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  formatStreams(value: number | null | undefined): string {
    return formatNumber(value);
  }

  getArtistsList(artists: any[]): string {
    if (!artists || !artists.length) return '';
    return artists.map(a => a.name).join(', ');
  }

  onTrackClick(songId: number): void {
    this.songClick.emit(String(songId));
  }

  albumImageFallback(title: string): string {
    const n = (title || 'A').trim() || 'A';
    return `https://placehold.co/300x300/1e293b/94a3b8?text=${encodeURIComponent(n.substring(0, 10))}`;
  }

  getFormattedReleaseType(album: any): string {
    if (!album) return '';
    const rawType = (album.type || '').toLowerCase();
    const tracks = album.songs || [];
    const trackCount = tracks.length;
    const title = (album.title || '').toLowerCase();

    const totalDurationMs = tracks.reduce((sum: number, s: any) => sum + (s.durationMs || s.duration_ms || 0), 0);
    const totalMinutes = totalDurationMs / (1000 * 60);

    const isEpByDefinition = trackCount >= 4 && trackCount <= 6 && (totalMinutes === 0 || totalMinutes < 30);

    if (rawType === 'single') {
      if (isEpByDefinition) {
        return 'EP';
      }
      return 'Single';
    }
    if (rawType === 'album') {
      if (isEpByDefinition || (trackCount >= 4 && trackCount <= 6 && title.includes('ep'))) {
        return 'EP';
      }
      return 'Album';
    }
    if (rawType === 'ep') {
      return 'EP';
    }
    if (rawType === 'appears_on') {
      return 'Appears On';
    }
    return album.type || '';
  }
}
